/**
 * CollapsibleCard — Sprint 12 Track 20 Phase 1.
 *
 * Reusable wrapper for collapsible Team Log message cards. Extracted from
 * BuildCompleteCard's collapse pattern to support delegation-plan,
 * state-capsule, and auto-collapse heuristic messages.
 *
 * Auto-collapse triggers:
 * - Always: build-complete, delegation-plan, state-capsule tags
 * - Heuristic: >20 lines OR contains ## markdown headers
 */

import { useState } from "react";
import type { TeamLogMessage } from "../types";

interface CollapsibleCardProps {
  message: TeamLogMessage;
  defaultExpanded?: boolean;
  header: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function CollapsibleCard({
  message,
  defaultExpanded = false,
  header,
  children,
  className,
}: CollapsibleCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div
      className={`collapsible-card-shell ${className ?? ""}`}
      data-expanded={expanded}
      data-message-id={message.id}
      onClick={(e) => {
        // Only toggle when click is on the shell itself or the header,
        // not on interior interactive elements (button, link, input, textarea).
        const target = e.target as HTMLElement;
        if (target.closest("button, a, input, textarea")) return;
        setExpanded((v) => !v);
      }}
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderLeft: "3px solid var(--er-accent)",
        borderRadius: 8,
        overflow: "hidden",
        cursor: expanded ? "default" : "pointer",
      }}
    >
      <div
        className="collapsible-card-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
        }}
      >
        {header}
        <span className={`caret${expanded ? " is-open" : ""}`} aria-hidden="true" style={{ marginLeft: 8 }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M3 2 L7 5 L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
      {expanded && <div className="collapsible-card-content">{children}</div>}
    </div>
  );
}
